import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://earnmate.in";
const DEFAULT_OG = `${BASE_URL}/og-image.png`;
const DEFAULT_DESC = "EarnMate is India's mobile-first marketplace for event staffing and freelance gigs. Hire instantly. Earn instantly.";

const ROUTES = {
  "/": { title: "EarnMate — Event Staffing & Freelance Marketplace in India", description: "EarnMate is India's fastest growing marketplace for event staffing and freelance gigs. Hire catering staff, security, cameramen, designers, developers — or post your skills and earn instantly.", noIndex: false },
  "/jobs": { title: "Browse Jobs — Event Gigs & Freelance Work | EarnMate", description: "Browse event staffing and freelance jobs in India. Find catering, security, photography, design, development gigs near you on EarnMate.", noIndex: false },
  "/freelancers": { title: "Hire Freelancers — Designers, Developers & More | EarnMate", description: "Find skilled freelancers for your projects on EarnMate. Hire designers, developers, writers, photographers and more across India.", noIndex: false },
  "/post-job": { title: "Post a Job — Hire Event Staff or Freelancers | EarnMate", description: "Post a job on EarnMate and find the right person fast. Event staffing, freelance gigs, part-time work — post free and hire instantly.", noIndex: false },
  "/login": { title: "Log In | EarnMate", description: "Log in to your EarnMate account to manage your jobs, applications and profile.", noIndex: false },
  "/register": { title: "Sign Up Free — Join EarnMate", description: "Create your free EarnMate account. Find event staffing gigs, post freelance jobs, and connect with employers across India.", noIndex: false },
  "/about": { title: "About EarnMate — India's Event & Freelance Marketplace", description: "Learn about EarnMate — the mobile-first platform connecting event employers with skilled workers and freelancers across India.", noIndex: false },
  "/contact": { title: "Contact EarnMate", description: "Get in touch with the EarnMate team. We're here to help with job postings, freelancer profiles, and platform questions.", noIndex: false },
  "/faq": { title: "FAQ — Frequently Asked Questions | EarnMate", description: "Got questions about EarnMate? Find answers about hiring, earning, payments, and how the platform works.", noIndex: false },
  "/privacy-policy": { title: "Privacy Policy | EarnMate", description: "Read EarnMate's privacy policy to understand how we collect, use and protect your data.", noIndex: false },
  "/terms": { title: "Terms of Service | EarnMate", description: "Read EarnMate's terms of service and user agreement.", noIndex: false },
  "/cookies": { title: "Cookie Policy | EarnMate", description: "Read EarnMate's cookie policy.", noIndex: false },
  "/forgot-password": { title: "Forgot Password | EarnMate", noIndex: true },
  "/reset-password": { title: "Reset Password | EarnMate", noIndex: true },
  "/verify-email": { title: "Verify Email | EarnMate", noIndex: true },
  "/auth/callback": { title: "EarnMate", noIndex: true },
  "/complete-profile": { title: "Complete Your Profile | EarnMate", noIndex: true },
  "/dashboard": { title: "Dashboard | EarnMate", noIndex: true },
  "/notifications": { title: "Notifications | EarnMate", noIndex: true },
  "/messages": { title: "Messages | EarnMate", noIndex: true },
  "/admin": { title: "Admin | EarnMate", noIndex: true },
  "/checkin": { title: "Check In | EarnMate", noIndex: true },
  "/create-event": { title: "Create Event | EarnMate", noIndex: true },
};

function getSEO(pathname) {
  if (ROUTES[pathname]) return ROUTES[pathname];
  if (pathname.startsWith("/job/")) return { title: "Job Details | EarnMate", description: "View job details and apply on EarnMate.", noIndex: false };
  if (pathname.startsWith("/freelancer/")) { const u = pathname.split("/freelancer/")[1]; return { title: `${u} — Freelancer Profile | EarnMate`, description: `View ${u}'s freelancer profile on EarnMate.`, noIndex: false }; }
  if (pathname.startsWith("/profile/") || pathname.startsWith("/manager/")) { const u = pathname.split("/")[2]; return { title: `${u} | EarnMate`, description: `View ${u}'s profile on EarnMate.`, noIndex: false }; }
  if (pathname.startsWith("/events/")) return { title: "Event Details | EarnMate", description: "View event details on EarnMate.", noIndex: false };
  if (pathname.startsWith("/manage-event/") || pathname.startsWith("/manage/") || pathname.startsWith("/messages/")) return { title: "EarnMate", noIndex: true };
  return { title: "EarnMate — Event Staffing & Freelance Marketplace in India", description: DEFAULT_DESC, noIndex: false };
}

function setMeta(selector, attr, value) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const match = selector.match(/\[(.+?)="(.+?)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEOManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description, noIndex } = getSEO(pathname);
    const desc = description || DEFAULT_DESC;
    const canonical = `${BASE_URL}${pathname === "/" ? "" : pathname}`;
    document.title = title;
    setMeta('meta[name="title"]', "content", title);
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[name="robots"]', "content", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    setLink("canonical", canonical);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", DEFAULT_OG);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", DEFAULT_OG);
  }, [pathname]);

  return null;
}
